import { useEffect, useRef } from 'react';

const COLORS = ['#14564b', '#b8915a', '#2a6f50', '#7fd7c4'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  vr: number;
  color: string;
}

interface ConfettiProps {
  scale?: number;
  lifetimeFrames?: number;
  particleCount?: number;
}

export default function Confetti({
  scale = 1,
  lifetimeFrames = 150,
  particleCount = 90,
}: ConfettiProps = {}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const parent = canvas.parentElement;
    const width = (canvas.width = parent?.clientWidth ?? window.innerWidth);
    const height = (canvas.height = parent?.clientHeight ?? 400);

    const particles: Particle[] = Array.from({ length: particleCount }, () => ({
      x: width / 2 + (Math.random() - 0.5) * width * 0.35,
      y: height * 0.18,
      vx: (Math.random() - 0.5) * 8 * scale,
      vy: (Math.random() * -9 - 2) * scale,
      w: 5 + Math.random() * 5,
      h: 7 + Math.random() * 6,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
    }));

    const gravity = 0.32 * scale;
    let animationFrame = 0;
    let frameNumber = 0;

    function drawFrame() {
      frameNumber += 1;
      ctx!.clearRect(0, 0, width, height);
      const alpha = Math.max(0, 1 - frameNumber / lifetimeFrames);
      for (const particle of particles) {
        particle.vy += gravity;
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.rot += particle.vr;
        ctx!.save();
        ctx!.globalAlpha = alpha;
        ctx!.translate(particle.x, particle.y);
        ctx!.rotate(particle.rot);
        ctx!.fillStyle = particle.color;
        ctx!.fillRect(-particle.w / 2, -particle.h / 2, particle.w, particle.h);
        ctx!.restore();
      }
      if (frameNumber < lifetimeFrames) {
        animationFrame = requestAnimationFrame(drawFrame);
      } else {
        ctx!.clearRect(0, 0, width, height);
      }
    }

    animationFrame = requestAnimationFrame(drawFrame);
    return () => cancelAnimationFrame(animationFrame);
  }, [lifetimeFrames, particleCount, scale]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
    />
  );
}
